import moment, {Moment} from "moment";
import {ITableItem} from 'shared-types/index';
import {IServiceItem} from "app/app.types";
import {IBooking} from 'shared-types/SharedTypes'
import DateUtilsService from 'external-services/date-utils-service';

const NS = 'TableDragService';

class TableDragService {

  // Table Join Drag Functions

  getAppropriateTable(toBooking: IBooking, fromBooking: IBooking) {
    const tableCapacity: number[] = [];
    toBooking.tables.forEach((table) => {
      tableCapacity.push(table.capacity);
    });

    const toTablesArray = toBooking.tables.find(table => table.capacity === this.findClosest(tableCapacity, fromBooking.people));
    return toTablesArray ? [toTablesArray] : [toBooking.tables[0]];
  }

  // to get the array of tables where the dragBooking is dropped, where the number of tables selected depends on the capacity of total people in the booking and/or the max number of tables in the dragBooking
  getConsecutiveTables(fromTableBooking: IBooking, toTable: ITableItem, tablesList: ITableItem[]) {
    const dragBookingTables = fromTableBooking.tables;
    const totalPeople = fromTableBooking.people;
    const sectionTables = tablesList.filter(table => toTable.sectionId === table.sectionId);
    const dropTableIndex = sectionTables.findIndex(table => toTable._id === table._id);
    const dropTableArray = [];
    let tableCapacity = 0;
    for (let i = dropTableIndex; i < dropTableIndex + dragBookingTables.length; i++) {
      if (tableCapacity < totalPeople) {
        dropTableArray.push(sectionTables[i])
      }
      tableCapacity += sectionTables[i].capacity;
    }
    return dropTableArray;
  }

  isOverlapBooking(bookingsByTable: any, tableArr: ITableItem[], dragBooking: IBooking, dropBooking: {time: Date, _id: string}, areBookingsSwapped: boolean) {
    const time = areBookingsSwapped ? dragBooking.time : dropBooking.time;
    const bookingsByTableArray = tableArr.map(table => bookingsByTable[table._id]);
    const bookingStartTime = moment(time);
    const bookingEndTime = moment(time).add(dragBooking.duration, 'minutes');
    const overlapBookings = bookingsByTableArray && bookingsByTableArray.length > 0 ?
      bookingsByTableArray.filter(bookingByTable => this.getOverlapBookings(bookingByTable, bookingStartTime, bookingEndTime, dragBooking, dropBooking._id)) : [];
    return overlapBookings.length > 0;
  }

  /**
   * Like isOverlapBooking, but shortens the dragged booking's duration if the dropTime is during the dragged booking's
   * start and end time.
   */
  isOverlapOnProgressedBooking(bookingsByTable: any, tableArr: ITableItem[], dragBooking: IBooking, dropTime: Date) {
    const dropBookingPartial: {time: Date, _id: string} = {
      _id: undefined, // doesn't need _id because drag isn't possible on table joins anyway (@todo: double check this)
      time: dropTime
    };
    const dragBookingClone = {...dragBooking};
    if (dropTime > dragBookingClone.time && dropTime < dragBookingClone.bookingEnd) {
      // we only need the remaining duration of the dragged booking, which will be less if already started
      dragBookingClone.duration = DateUtilsService.minsBetweenDates(dropTime, dragBookingClone.bookingEnd);
    }
    return this.isOverlapBooking(bookingsByTable, tableArr, dragBookingClone, dropBookingPartial, false);
  }

  checkForErrorWhileDraggingTables(fromTableBooking: IBooking, toTable: ITableItem, tablesList: ITableItem[]) {
    const dragBookingTables = fromTableBooking.tables;
    const sectionTables = tablesList.filter(table => toTable.sectionId === table.sectionId)
    const dropTableIndex = sectionTables.findIndex(table => toTable._id === table._id);
    return (dropTableIndex + dragBookingTables.length) > sectionTables.length;
  }

  /**
   * Compare the drag and drop booking tables, to check whether swapping booking on the same table
   */
  areSwappingBookingsOnSameTable(fromTableArray: ITableItem[], toTablesArray: ITableItem[]) {
    return fromTableArray.some(fromTable => {
      return toTablesArray.some(toTable => toTable._id === fromTable._id);
    });
  }

  /**
   * Check whether the drop booking time falls within the service time (i.e. between startTime and endTime)
   */
  doesBookingTimeFallWithinService(newTime: Date, fromTable: ITableItem, toTable: ITableItem, dragBooking: IBooking, services: IServiceItem[]): boolean {

    // Drag an drop table must be in same section
    if (toTable.sectionId !== fromTable.sectionId) return false;

    // Retrieves the service of the drag booking
    const fromService = services.find(service => service._id === dragBooking.serviceId);
    if (!fromService) return false;

    return DateUtilsService.isInBetweenDates(newTime, fromService.startTime, fromService.endTime);
  }

  private getOverlapBookings(bookingByTable: any, bookingStartTime: Moment, bookingEndTime: Moment, dragBooking: IBooking, dropBookingId?: string) {
    let isOverlap = false;
    if (bookingByTable && bookingByTable.bookings.length > 0) {
      bookingByTable.bookings.forEach((booking: IBooking) => {
        if (booking._id != dragBooking._id && booking._id != dropBookingId) {
          const tableBookingStartTime = moment(booking.time);
          const tableBookingEndTime = moment(booking.time).add(booking.duration, 'minutes');
          if (!isOverlap) {
            // Check the overlap for both the dragged as well dropped booking
            const isDroppedBookingOverlapping = tableBookingStartTime.isBetween(bookingStartTime, bookingEndTime, null, '[)') || tableBookingEndTime.isBetween(bookingStartTime, bookingEndTime, null, '(]');
            const isDraggedBookingOverlapping = bookingStartTime.isBetween(tableBookingStartTime, tableBookingEndTime, null, '[)') || bookingEndTime.isBetween(tableBookingStartTime, tableBookingEndTime, null, '(]');
            isOverlap = isDroppedBookingOverlapping || isDraggedBookingOverlapping;
          }
        }
      });
    }
    return isOverlap;
  }

  private findClosest(arr: any[], closestTo: number) {
    let closest = Math.max.apply(null, arr); //Get the highest number in arr in case it match nothing.
    for (let i = 0; i < arr.length; i++) { //Loop the array
      if (arr[i] >= closestTo && arr[i] < closest) closest = arr[i]; //Check if it's higher than your number, but lower than your closest value
    }
    return closest; // return the value
  }
}

// creates a singleton instance to export
const instance = new TableDragService();
export {instance as TableDragService};
